import { supabase } from '@/lib/supabase/client';
import type { Enums, Tables } from '@/lib/supabase/types';
import { queryOptions } from '@tanstack/react-query';

export type WorkspaceRow = Tables<'workspaces'>;
export type WorkspaceMemberRow = Tables<'workspace_members'>;

export type MemberWithProfile = WorkspaceMemberRow & {
  profile: Pick<
    Tables<'profiles'>,
    'id' | 'display_name' | 'avatar_url' | 'email' | 'blocked_at'
  > | null;
};

export const workspacesKeys = {
  all: ['workspaces'] as const,
  mine: ['workspaces', 'mine'] as const,
  bySlug: (slug: string) => ['workspaces', 'slug', slug] as const,
  members: (workspaceId: string) => ['workspaces', workspaceId, 'members'] as const,
};

export function listMyWorkspacesQuery() {
  return queryOptions({
    queryKey: workspacesKeys.mine,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workspaces')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as WorkspaceRow[];
    },
    staleTime: 5 * 60_000,
  });
}

export function getWorkspaceBySlugQuery(slug: string) {
  return queryOptions({
    queryKey: workspacesKeys.bySlug(slug),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('workspaces')
        .select('*')
        .eq('slug', slug)
        .maybeSingle();
      if (error) throw error;
      return data as WorkspaceRow | null;
    },
    staleTime: 5 * 60_000,
  });
}

export function listMembersQuery(workspaceId: string) {
  return queryOptions({
    queryKey: workspacesKeys.members(workspaceId),
    queryFn: async () => {
      // workspace_members tiene 2 FKs a profiles (user_id, invited_by) — disambiguar
      // explicitamente con el nombre del FK constraint para evitar 300 Multi-Choice.
      // NO filtramos blocked_at aca: queremos que computeOrphanAssignees pueda
      // resolver el profile de assignees existentes que despues fueron bloqueados
      // (sino se clasifican como orphans incorrectamente). El filtro de pickers /
      // mentions vive en filterEligibleMembers (project-eligibility.ts).
      const { data, error } = await supabase
        .from('workspace_members')
        .select(
          `
          workspace_id, user_id, role, joined_at, invited_by, access_scope, hidden_from_pickers,
          profile:profiles!workspace_members_user_id_fkey(id, display_name, avatar_url, email, blocked_at)
          `,
        )
        .eq('workspace_id', workspaceId);
      if (error) throw error;
      return (data ?? []) as unknown as MemberWithProfile[];
    },
    staleTime: 60_000,
  });
}

/**
 * Lista los project_ids a los que un miembro tiene acceso explícito en este
 * workspace (filas de project_members joineadas a projects para asegurar que
 * los proyectos pertenezcan al workspace dado). Usado por el modal de
 * "Editar acceso" para prepoblar los checkboxes con la selección actual.
 */
export function listMemberProjectAccessQuery(workspaceId: string, userId: string) {
  return queryOptions({
    queryKey: ['workspaces', workspaceId, 'members', userId, 'project-access'] as const,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_members')
        .select('project_id, projects!inner(workspace_id)')
        .eq('user_id', userId)
        .eq('projects.workspace_id', workspaceId);
      if (error) throw error;
      return (data ?? []).map((r) => r.project_id as string);
    },
    enabled: !!workspaceId && !!userId,
    staleTime: 30_000,
  });
}

export type WorkspaceRole = Enums<'workspace_role'>;

/** Workspaces where a given user is a member. Two-step: get membership rows, then workspace rows. */
export function listUserWorkspacesQuery(userId: string) {
  return queryOptions({
    queryKey: ['workspaces', 'user', userId] as const,
    queryFn: async () => {
      if (!userId) return [] as (WorkspaceRow & { role: WorkspaceRole })[];

      // Step 1: get the user's memberships
      const { data: memberships, error: mErr } = await supabase
        .from('workspace_members')
        .select('workspace_id, role')
        .eq('user_id', userId);
      if (mErr) throw mErr;
      if (!memberships || memberships.length === 0) return [];

      const wsIds = memberships.map((m) => m.workspace_id);
      const roleMap = Object.fromEntries(memberships.map((m) => [m.workspace_id, m.role]));

      // Step 2: fetch workspace rows
      const { data: workspaces, error: wsErr } = await supabase
        .from('workspaces')
        .select('*')
        .in('id', wsIds)
        .order('created_at', { ascending: false });
      if (wsErr) throw wsErr;

      return (workspaces ?? []).map((ws) => ({
        ...ws,
        role: (roleMap[ws.id] ?? 'member') as WorkspaceRole,
      })) as (WorkspaceRow & { role: WorkspaceRole })[];
    },
    staleTime: 5 * 60_000,
  });
}
